import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import "./TermsAndConditions.css";
import Markdown from "react-markdown";
import { Modal, Button, IconButton } from "@mui/material";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import { useAcceptanceHistoryQuery, useGetDocumentsInTermsAndConditionsQuery, useGetDocumentsQuery } from "../../services/documentsApi";
import CircularProgress from "@mui/material/CircularProgress";
import rehypeRaw from 'rehype-raw'
import gfm from 'remark-gfm';

import { marked } from "marked";
import html2pdf from "html2pdf.js";
import {  toStdDate } from "../../utils/dates";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../features/auth/authSlice";

const TermsAndConditionsReadOnly = ({ open, handleClose }) => {

  const user = useSelector(selectCurrentUser);
  
  const { data: documentsApiRes, isLoading , isFetching, isError } = useGetDocumentsInTermsAndConditionsQuery(null, {skip: !open});

  const [documentIndex, setDocumentIndex] = useState(0);

  const onClose = () => {
    setDocumentIndex(0);
    handleClose();
  }

  const documents = useMemo(() => {
    return (JSON.parse(JSON.stringify(documentsApiRes || []))).sort((a, b) => a.documentTypeID - b.documentTypeID);
  }, [documentsApiRes]);

  const SignedOn = ({userId, document}) => {
    const { data: documentsAccepted, isError: errorOnAcceptanceHistory } = useAcceptanceHistoryQuery(userId);
    if (document && documentsAccepted && !errorOnAcceptanceHistory && documentsAccepted.length > 0) {    
      let docAcc = documentsAccepted.findLast(acc => acc.documentTypeName === document.documentType.name);
      if (docAcc) {
        let formattedDate = toStdDate(docAcc.acceptedOn).split(" ")[0];
        return <p style={{ margin: "0 2em 0 0" }}>Signed on <b>{formattedDate}</b> </p>
      }
    }
    return null;
  }

  function ActiontButtons() {

    const handleDownload = () => {
      if (documents) {
        const htmlContent = marked.parse(documents[documentIndex]?.documentContent); // Convert Markdown to HTML
        const element = window.document.createElement("div");
        element.innerHTML = htmlContent;
        element.className = "pdf-content";

        // Ensure that your HTML has styles that respect the margins
        element.style.padding = "20mm 15mm";

        // Set up the options for html2pdf
        const options = {
          //margin: [10, 15],
          filename: 'EncoreHive-TermsAndConditions.pdf',
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: 'avoid-all' } // ['avoid-all', 'css', 'legacy']
        };
        // Use the options when generating the PDF
        html2pdf().from(element).set(options).save();
        console.log("Downloading PDF");
      }
    };

    const handlePrint = () => {
      if (documents) {
        const htmlContent = marked.parse(documents[documentIndex]?.documentContent); // Convert Markdown to HTML
        const printFrame = window.document.createElement('iframe');
        printFrame.setAttribute('allow-modals', 'true');
        printFrame.style.visibility = 'hidden';
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        window.document.body.appendChild(printFrame);
        printFrame.contentDocument.open();
        printFrame.contentDocument.write(htmlContent);
        printFrame.contentDocument.close();
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        window.document.body.removeChild(printFrame); // Remove the iframe after printing
      }
    }

    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "100%", width: "content-fit" }} >
        {
          user && user?.id &&
          <SignedOn userId={user?.id} document={documents[documentIndex]}/>
        }
        <div style={{ margin: "0 1em 0 0", display: "flex", justifyContent: "space-between", height: "100%", width: "content-fit" }} >
          <IconButton
            variant="contained"
            sx={{
              color: "white",
              borderRadius: "50%",
              margin: "0 0.5em 0 0",
              height: "1.6em",
              width: "1.6em"
            }}
            onClick={handleDownload}
          >
            <FileDownloadOutlinedIcon
              sx={{
                backgroundColor: "var(--orange)",
                borderRadius: "50%",
                height: "1.6em",
                width: "1.6em",
                padding: "0.2em"
              }}
            />
          </IconButton>
          <IconButton
            sx={{
              color: "white",
              borderRadius: "50%",
              margin: "0 0.5em 0 0",
              height: "1.6em",
              width: "1.6em"
            }}
            onClick={handlePrint}
          >
            <LocalPrintshopOutlinedIcon
              sx={{
                backgroundColor: "var(--orange)",
                borderRadius: "50%",
                height: "1.6em",
                width: "1.6em",
                padding: "0.2em"
              }}
            />
          </IconButton>
        </div>
        <Button disabled={isFetching} variant="outlined" className="button" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  function CicularProgressComp() {
    return (
      <CircularProgress sx={{
        color: "#F29B1B",
        /* Center vertically and horizontally */
        position: "absolute",
        top: "50%",
        left: "50%"
      }} />
    )
  }

  function NoDocumentsToShow() {
    return (
      <>
        <p>There is no document to show</p>
        <ActiontButtons></ActiontButtons>
      </>
    )
  }

  function TermsAndConditionsComp({ documentIndex }) {
    return (
      <>
        {documents[documentIndex]?.documentContent && (
          <Markdown 
            rehypePlugins={[rehypeRaw]} 
            remarkPlugins={[gfm]}
          >
            {documents[documentIndex].documentContent}
          </Markdown>
        )}
      </>
    )
  }

  const handlePreviousDocument = () => {
    if (documentIndex > 0) {
      setDocumentIndex(documentIndex - 1);
    }
  };

  const handleNextDocument = () => {
    if (documentIndex < documents.length - 1) {
      setDocumentIndex(documentIndex + 1);
    }
  };

  const loading= isLoading || isFetching;

  return (
    <Modal open={open} onClose={onClose} className="terms-and-conditions-modal">
      <div className="terms-and-conditions">
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:'center', width:'100%', margin:'0 0 1em 0' }} >
          {
            documentIndex > 0 ? <IconButton color="primary" onClick={handlePreviousDocument} ><ArrowBackIosOutlinedIcon /> </IconButton> : <div style={{width:"40px"}} ></div>
          }
          <p className="terms-and-conditions-title" >
            {documents?.[documentIndex]?.documentType?.name}
          </p>
          {
            documentIndex < documents?.length - 1 ? <IconButton color="primary" onClick={handleNextDocument}> <ArrowForwardIosOutlinedIcon/> </IconButton> : <div style={{width:"40px"}} ></div>
          }

        </div>

        <div className="terms-and-conditions-content">

          {loading && <CicularProgressComp />}
          {!loading && !documents && <NoDocumentsToShow />}
          {!loading && documents && <TermsAndConditionsComp documentIndex={documentIndex} />}
          {isError && <p>There was an error</p>}
        </div>
        {
          !loading && documents &&
          <div className="terms-and-conditions-user-input">
            <ActiontButtons></ActiontButtons>
          </div>
        }
      </div>
    </Modal>
  );
};

export default TermsAndConditionsReadOnly;